/**
 * Input actions, default bindings, and persistence (SPEC §3.13).
 *
 * Every binding is remappable and persisted. The remap *UI* is M10's job, but the data model
 * has to exist now or every later milestone hard-codes `event.code === 'KeyC'` somewhere and the
 * remap becomes a refactor instead of a screen.
 *
 * Keyboard bindings are `KeyboardEvent.code` values (physical keys), not `key` values, so the
 * layout works the same on AZERTY and QWERTY.
 */

export const InputAction = {
  RollLeft: 'rollLeft',
  RollRight: 'rollRight',
  PitchUp: 'pitchUp',
  PitchDown: 'pitchDown',
  YawLeft: 'yawLeft',
  YawRight: 'yawRight',
  ThrottleUp: 'throttleUp',
  ThrottleDown: 'throttleDown',
  Chaff: 'chaff',
  Flares: 'flares',
  CycleWaypoint: 'cycleWaypoint',
  ToggleAutopilot: 'toggleAutopilot',
  Zoom: 'zoom',
  LookAround: 'lookAround',
  PauseMenu: 'pauseMenu',
} as const;

export type InputActionId = (typeof InputAction)[keyof typeof InputAction];

export const ALL_ACTIONS: readonly InputActionId[] = Object.values(InputAction);

/** Actions that only become live in a later milestone, listed so nothing looks forgotten. */
export const UNIMPLEMENTED_ACTIONS: readonly InputActionId[] = [
  InputAction.Chaff, // M6
  InputAction.Flares, // M6
  InputAction.CycleWaypoint, // M8
  InputAction.Zoom, // M2
  InputAction.LookAround, // M2
  InputAction.PauseMenu, // M10
];

export type KeyboardBindings = Record<InputActionId, readonly string[]>;
export type GamepadButtonBindings = Partial<Record<InputActionId, readonly number[]>>;

export interface InputOptions {
  /** Pitch axis sense on stick and mouse; keyboard follows the same flag. */
  invertPitch: boolean;
  mouseEnabled: boolean;
  gamepadEnabled: boolean;
  mouseSensitivity: number;
  gamepadDeadzone: number;
  gamepadExpo: number;
}

export interface BindingsData {
  version: 1;
  keyboard: KeyboardBindings;
  gamepadButtons: GamepadButtonBindings;
  options: InputOptions;
}

/** Standard-gamepad button indices, for readability at the binding site. */
export const PadButton = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  Back: 8,
  Start: 9,
  LeftStick: 10,
  RightStick: 11,
  DpadUp: 12,
  DpadDown: 13,
  DpadLeft: 14,
  DpadRight: 15,
} as const;

/** Standard-gamepad axis indices. */
export const PadAxis = {
  LeftX: 0,
  LeftY: 1,
  RightX: 2,
  RightY: 3,
} as const;

const STORAGE_KEY = 'vectorzero.bindings.v1';

export function defaultKeyboardBindings(): KeyboardBindings {
  return {
    [InputAction.RollLeft]: ['KeyA'],
    [InputAction.RollRight]: ['KeyD'],
    // Stick sense: W is forward, so W lowers the nose. Invertible via options.
    [InputAction.PitchDown]: ['KeyW'],
    [InputAction.PitchUp]: ['KeyS'],
    [InputAction.YawLeft]: ['KeyQ'],
    [InputAction.YawRight]: ['KeyE'],
    [InputAction.ThrottleUp]: ['ShiftLeft', 'ShiftRight'],
    [InputAction.ThrottleDown]: ['ControlLeft', 'ControlRight'],
    [InputAction.Chaff]: ['KeyC'],
    [InputAction.Flares]: ['KeyV'],
    [InputAction.CycleWaypoint]: ['Tab'],
    [InputAction.ToggleAutopilot]: ['KeyH'],
    [InputAction.Zoom]: ['KeyZ'],
    [InputAction.LookAround]: [],
    [InputAction.PauseMenu]: ['Escape'],
  };
}

export function defaultGamepadButtons(): GamepadButtonBindings {
  return {
    [InputAction.Chaff]: [PadButton.LB],
    [InputAction.Flares]: [PadButton.RB],
    [InputAction.CycleWaypoint]: [PadButton.DpadRight],
    [InputAction.ToggleAutopilot]: [PadButton.DpadUp],
    [InputAction.Zoom]: [PadButton.RightStick],
    [InputAction.LookAround]: [PadButton.LeftStick],
    [InputAction.PauseMenu]: [PadButton.Start],
  };
}

export function defaultOptions(): InputOptions {
  return {
    invertPitch: false,
    mouseEnabled: true,
    gamepadEnabled: true,
    mouseSensitivity: 1,
    gamepadDeadzone: 0.15,
    gamepadExpo: 0.35,
  };
}

export function defaultBindings(): BindingsData {
  return {
    version: 1,
    keyboard: defaultKeyboardBindings(),
    gamepadButtons: defaultGamepadButtons(),
    options: defaultOptions(),
  };
}

export class Bindings {
  private data: BindingsData = defaultBindings();

  /** Reverse index from key code to actions, rebuilt on every change. */
  private keyToActions = new Map<string, InputActionId[]>();

  constructor(data?: BindingsData) {
    if (data !== undefined) this.data = data;
    this.rebuildIndex();
  }

  get options(): InputOptions {
    return this.data.options;
  }

  get keyboard(): KeyboardBindings {
    return this.data.keyboard;
  }

  get gamepadButtons(): GamepadButtonBindings {
    return this.data.gamepadButtons;
  }

  /** Actions bound to a physical key code, or an empty list. */
  actionsForKey(code: string): readonly InputActionId[] {
    return this.keyToActions.get(code) ?? EMPTY_ACTIONS;
  }

  /** True if any key bound to the action is in the pressed set. */
  isActionPressed(action: InputActionId, pressedCodes: ReadonlySet<string>): boolean {
    const codes = this.data.keyboard[action];
    for (let i = 0; i < codes.length; i += 1) {
      if (pressedCodes.has(codes[i])) return true;
    }
    return false;
  }

  /** Replace the keys bound to an action. */
  setKeyBinding(action: InputActionId, codes: readonly string[]): void {
    this.data.keyboard[action] = [...codes];
    this.rebuildIndex();
  }

  setOption<K extends keyof InputOptions>(key: K, value: InputOptions[K]): void {
    this.data.options[key] = value;
  }

  resetToDefaults(): void {
    this.data = defaultBindings();
    this.rebuildIndex();
  }

  toJSON(): BindingsData {
    return this.data;
  }

  /**
   * Persist to localStorage. Silently does nothing where storage is unavailable (private mode,
   * headless tests) — bindings are a convenience, and failing to save one must never break a
   * flight.
   */
  save(): boolean {
    const storage = safeStorage();
    if (storage === null) return false;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Load persisted bindings, falling back to defaults for anything missing or malformed. A
   * corrupt entry must not leave the player unable to fly, so unknown actions are dropped and
   * missing ones are filled from defaults rather than rejected wholesale.
   */
  static load(): Bindings {
    const storage = safeStorage();
    if (storage === null) return new Bindings();
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (raw === null) return new Bindings();
      const parsed: unknown = JSON.parse(raw);
      return new Bindings(mergeWithDefaults(parsed));
    } catch {
      return new Bindings();
    }
  }

  private rebuildIndex(): void {
    this.keyToActions.clear();
    for (const action of ALL_ACTIONS) {
      for (const code of this.data.keyboard[action]) {
        const existing = this.keyToActions.get(code);
        if (existing === undefined) this.keyToActions.set(code, [action]);
        else existing.push(action);
      }
    }
  }
}

const EMPTY_ACTIONS: readonly InputActionId[] = [];

/** localStorage is an external boundary: it may be absent, or throw on access. */
function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Merge untrusted parsed JSON over the defaults, keeping only recognised shapes. */
export function mergeWithDefaults(parsed: unknown): BindingsData {
  const result = defaultBindings();
  if (typeof parsed !== 'object' || parsed === null) return result;

  // The parsed value is untrusted JSON from storage — a genuine external boundary, so it is
  // walked as an unknown record and each field is checked before it is trusted.
  const candidate: Record<string, unknown> = parsed as Record<string, unknown>;

  const keyboard = candidate['keyboard'];
  if (typeof keyboard === 'object' && keyboard !== null) {
    const keyboardRecord = keyboard as Record<string, unknown>;
    for (const action of ALL_ACTIONS) {
      const codes = keyboardRecord[action];
      if (Array.isArray(codes) && codes.every((code) => typeof code === 'string')) {
        result.keyboard[action] = codes as string[];
      }
    }
  }

  const options = candidate['options'];
  if (typeof options === 'object' && options !== null) {
    const optionsRecord = options as Record<string, unknown>;
    const defaults = result.options as unknown as Record<string, unknown>;
    for (const key of Object.keys(defaults)) {
      const value = optionsRecord[key];
      // Same primitive type as the default, or it is discarded.
      if (value !== undefined && typeof value === typeof defaults[key]) {
        defaults[key] = value;
      }
    }
  }

  return result;
}
